import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataTable as DDMDataTable } from "@ddm-design-system/data-table";
import Routes from "../../../routes";
import useContent from "../../../hooks/useContent";
import Header from "../../common/header/Header";
import Search from "../../common/search/Search";
import { customStyles } from "../../common/data_table/DataTable";
import { LoadingIcon } from "../../common/data_table/dataTable.styles";
import { Link } from "../../common/link/link.styles";
import { PressureChamberIcon } from "../installation/installation.styles";
import { getCurrentLanguage } from "../../../store/content/selectors";
import { getX10Devices } from "../../../store/x10devices/actions";
import {
  getX10DeviceQuery,
  getX10DeviceRows,
  getX10DevicesError,
  getX10DevicesLoading,
  getX10DevicesTotal,
  IX10DeviceRow
} from "../../../store/x10devices/selectors";
import { IX10DeviceQuery, X10DeviceSort } from "../../../store/x10devices/types";
import X10ViewSwitch from "./X10ViewSwitch";
import useDeviceListText from "./useDeviceListText";
import { formatLastHeard, machinesBadge } from "./helpers";
import {
  Badge,
  Control,
  FilterChip,
  MachineLink,
  Muted,
  OutletButton,
  OutletCell,
  SearchBox,
  StatusIcons,
  Summary,
  Toolbar
} from "./devices.styles";

const PAGE_SIZES = [25, 50, 100, 200];

/**
 * Device-first X10 list: every machine the user may see, one row each, searchable by MAC, machine, outlet or
 * distributor name, or the start of an id. The outlet is a tag; clicking it narrows the list to that outlet.
 */
const X10Devices: React.FC = () => {
  const dispatch = useDispatch();
  const text = useDeviceListText();
  const { common } = useContent();
  const language = useSelector(getCurrentLanguage);
  const query = useSelector(getX10DeviceQuery);
  const rows = useSelector(getX10DeviceRows);
  const total = useSelector(getX10DevicesTotal);
  const loading = useSelector(getX10DevicesLoading);
  const error = useSelector(getX10DevicesError);
  const [now, setNow] = useState(() => Date.now());

  const setQuery = useCallback(
    (change: Partial<IX10DeviceQuery>) => dispatch(getX10Devices(change)),
    [dispatch]
  );

  // Fetch with the remembered query when the page opens, so coming back keeps the search.
  useEffect(() => {
    dispatch(getX10Devices());
  }, [dispatch]);

  // Keep "last heard" ages current without refetching.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  // Narrowing to an outlet shows all of its machines, so the search that led there is cleared.
  const filterByOutlet = useCallback(
    (row: IX10DeviceRow) =>
      setQuery({ locationId: row.outletId, locationName: row.outletName, search: "" }),
    [setQuery]
  );

  const columns = useMemo(
    () => [
      {
        name: text("column_machine", "Machine"),
        selector: (row: IX10DeviceRow) => row.label,
        format: (row: IX10DeviceRow) => (
          <MachineLink to={row.link} title={row.label}>
            {row.label}
          </MachineLink>
        ),
        grow: 1.2
      },
      {
        name: common.common_serial_number || "Serial number",
        selector: (row: IX10DeviceRow) => row.boardSerialNumber
      },
      {
        name: common.common_outlet || "Outlet",
        selector: (row: IX10DeviceRow) => row.outletName,
        format: (row: IX10DeviceRow) => {
          const badge = machinesBadge(row.machines, row.liveMachines, query.live, text);

          return (
            <OutletCell>
              <OutletButton
                type="button"
                title={text("filter_outlet_hint", "Show only this outlet's machines")}
                onClick={() => filterByOutlet(row)}
              >
                {row.outletName}
              </OutletButton>
              {badge && <Badge tone="warning">{badge}</Badge>}
              {!row.outletIsX10 && (
                <Badge title={text("not_x10_outlet_hint", "Not shown in the Extra 10 outlet list")}>
                  {text("not_x10_outlet", "not an X10 outlet")}
                </Badge>
              )}
            </OutletCell>
          );
        },
        grow: 1.6
      },
      {
        name: common.common_status || "Status",
        selector: (row: IX10DeviceRow) => (row.alive ? 1 : 0),
        format: (row: IX10DeviceRow) => (
          <StatusIcons>
            {row.status.map(s => (
              <div key={s.icon} title={s.title}>
                <PressureChamberIcon size={16} name={s.icon} level={s.level} />
              </div>
            ))}
          </StatusIcons>
        ),
        width: "90px"
      },
      {
        name: text("column_last_heard", "Last heard"),
        selector: (row: IX10DeviceRow) => row.lastHeardAt || 0,
        format: (row: IX10DeviceRow) => (
          <span title={row.lastHeardAt ? new Date(row.lastHeardAt).toLocaleString(language) : ""}>
            {formatLastHeard(row.lastHeardAt, now, language)}
          </span>
        )
      },
      {
        name: common.common_brand || "Beverage",
        selector: (row: IX10DeviceRow) => row.brand as any,
        isImageSelector: true
      },
      {
        name: text("column_keg", "Keg"),
        selector: (row: IX10DeviceRow) => row.kegLiters,
        width: "90px"
      },
      {
        name: text("column_firmware", "Firmware"),
        selector: (row: IX10DeviceRow) => row.firmware,
        width: "100px"
      },
      {
        name: text("column_country", "Country"),
        selector: (row: IX10DeviceRow) => row.country,
        width: "90px"
      }
    ],
    [common, filterByOutlet, language, now, query.live, text]
  );

  // Remount the table when the result set changes so its internal page goes back to the first one.
  const tableKey = `${query.search}|${query.live}|${query.locationId}|${query.sort}|${query.size}`;

  const summary = error
    ? text("error", "The machine list could not be loaded. Try again in a moment.")
    : total === 1
    ? text("summary_one", "1 machine")
    : text("summary", "%N% machines").replace("%N%", String(total));

  return (
    <div className="pb-xl">
      <Header title={text("title", "Extra 10 machines")} />
      <X10ViewSwitch current="machines" />

      <Toolbar>
        <SearchBox>
          <Search
            // Remount when the outlet filter changes: picking an outlet clears the search text too.
            key={`search-${query.locationId}`}
            initialValue={query.search}
            onChange={value => setQuery({ search: value })}
            placeholder={text(
              "search_placeholder",
              "Search MAC / serial number, machine, outlet, distributor or id"
            )}
          />
        </SearchBox>
        <Control>
          <input
            type="checkbox"
            checked={query.live}
            onChange={event => setQuery({ live: event.target.checked })}
          />
          {text("live_only", "Only heard in the last 24 h")}
        </Control>
        <Control>
          {text("sort", "Sort by")}
          <select
            value={query.sort}
            onChange={event => setQuery({ sort: event.target.value as X10DeviceSort })}
          >
            <option value="OUTLET">{text("sort_outlet", "Outlet")}</option>
            <option value="NAME">{text("sort_name", "Machine")}</option>
            <option value="LAST_HEARD">{text("sort_last_heard", "Last heard")}</option>
          </select>
        </Control>
      </Toolbar>

      <Summary>
        <span>{summary}</span>
        {query.locationId && (
          <>
            <FilterChip>
              {text("outlet_filter", "Outlet: %NAME%").replace(
                "%NAME%",
                query.locationName || query.locationId
              )}
              <button
                type="button"
                aria-label={text("clear_outlet_filter", "Show all outlets")}
                title={text("clear_outlet_filter", "Show all outlets")}
                onClick={() => setQuery({ locationId: null, locationName: null })}
              >
                ×
              </button>
            </FilterChip>
            <Link to={Routes.extra10OutletDetails.replace(":id", query.locationId)}>
              {text("open_outlet", "Open outlet page")}
            </Link>
          </>
        )}
        {loading && rows.length > 0 && <Muted>{text("loading", "Updating…")}</Muted>}
      </Summary>

      <DDMDataTable
        key={tableKey}
        columns={columns as any}
        customStyles={customStyles}
        data={rows}
        noHeader
        progressComponent={<LoadingIcon />}
        progressPending={loading && rows.length === 0 && !error}
        noDataComponent={
          <Muted style={{ padding: 24 }}>
            {error
              ? summary
              : text(
                  "empty",
                  "No machines match. Try another search or include machines not heard in 24 h."
                )}
          </Muted>
        }
        pagination
        paginationServer
        paginationTotalRows={total}
        paginationPerPage={query.size}
        paginationDefaultPage={query.page + 1}
        paginationRowsPerPageOptions={PAGE_SIZES}
        onChangePage={(page: number) => setQuery({ page: page - 1 })}
        onChangeRowsPerPage={(size: number) => setQuery({ size })}
      />
    </div>
  );
};

export default X10Devices;
